import { GroupIds } from "@/shared/group-configs/groups";

export const DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS = 90;
export const DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE = 7;

/**
 * Largest routine window that is fully searchable against the EHR's availability horizon.
 *
 * The EHR layer caps every requested end date at `today + 6 months - 1 day`
 * (`resolveSchedulingEndDateString`), which lands 180–183 days out depending on which
 * calendar months the span covers. 180 is the floor of that range, so it is the largest
 * window that is searchable in *every* month.
 *
 * Configuring a window past this cap creates a silent dead zone: a custom date request
 * beyond the cap keeps its start date but has its end date clamped below it, so the
 * availability search resolves to start > end, scans nothing, and returns no slots with
 * no error (see the `startDate > endDate` skips in the Cobalt/webecw availability paths).
 */
export const MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS = 180;

export type LookupWindow = {
  startOffsetDays: number;
  maxWindowDays: number;
  /** When true, the routine start-offset floor is enforced even when the patient provides a custom date. */
  enforceWindowOnCustomDate?: boolean;
};

/** Urgent/STAT default: tomorrow through {@link DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE} days out. */
export const DEFAULT_PRIORITY_LOOKUP_WINDOW: LookupWindow = {
  startOffsetDays: 1,
  maxWindowDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
};

/** Per-group urgent/STAT lookup window. Groups not listed fall back to {@link DEFAULT_PRIORITY_LOOKUP_WINDOW}. */
const PRIORITY_LOOKUP_WINDOW_BY_GROUP_ID: Record<string, LookupWindow> = {
  // Balance Health: urgent/STAT referrals are same-day or one day after (same-day scheduling allowed);
  // routine picks up everything after (see ROUTINE_LOOKUP_WINDOW_BY_GROUP_ID below).
  [GroupIds.BALANCE_HEALTH_LIVE]: { startOffsetDays: 0, maxWindowDays: 1 },
  [GroupIds.BALANCE_HEALTH_LIVE_SANDBOX]: { startOffsetDays: 0, maxWindowDays: 1 },
  // USHV-Pima: urgent/STAT referrals look up availability today or one day after; routine uses the shared default.
  [GroupIds.USHV_PIMA_LIVE]: { startOffsetDays: 0, maxWindowDays: 1 },
  [GroupIds.USHV_PIMA_LIVE_SANDBOX]: { startOffsetDays: 0, maxWindowDays: 1 },
  // USHV-MKC: urgent/STAT referrals search tomorrow through seven days out.
  [GroupIds.USHV_MKC_LIVE]: { startOffsetDays: 1, maxWindowDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE },
  [GroupIds.USHV_MKC_LIVE_SANDBOX]: {
    startOffsetDays: 1,
    maxWindowDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
  },
  // TPR: urgent/STAT referrals must be scheduled within seven days.
  [GroupIds.TPR_BILTMORE_LIVE]: { startOffsetDays: 0, maxWindowDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE },
  [GroupIds.TPR_BILTMORE_LIVE_SANDBOX]: { startOffsetDays: 0, maxWindowDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE },
};

export function getPriorityLookupWindow(groupId: string): LookupWindow {
  return PRIORITY_LOOKUP_WINDOW_BY_GROUP_ID[groupId] ?? DEFAULT_PRIORITY_LOOKUP_WINDOW;
}

/**
 * Routine default: the leading days (up to {@link DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE}) are reserved so
 * urgent/STAT referrals get the soonest slots; routine runs from there through the 90-day max.
 */
export const DEFAULT_ROUTINE_LOOKUP_WINDOW: LookupWindow = {
  startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
  maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS,
};

/** Per-group routine lookup window. Groups not listed fall back to {@link DEFAULT_ROUTINE_LOOKUP_WINDOW}. */
const ROUTINE_LOOKUP_WINDOW_BY_GROUP_ID: Record<string, LookupWindow> = {
  // Balance Health: urgent/STAT covers same-day + one day after (0–1 days out), so routine starts the day
  // after that (2 days out) — "anything after" the STAT window — through a 30-day cap.
  [GroupIds.BALANCE_HEALTH_LIVE]: { startOffsetDays: 2, maxWindowDays: 30 },
  [GroupIds.BALANCE_HEALTH_LIVE_SANDBOX]: { startOffsetDays: 2, maxWindowDays: 30 },
  // Heartplace: 7-day start delay (reserved for urgent/STAT); routine searches reach up to six months out
  // (capped at the EHR's availability horizon, {@link MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS}). The option search
  // scans 90-day windows chronologically and stops once enough options are found, so 90 days stays the first
  // pass. enforceWindowOnCustomDate ensures the 7-day floor is applied even when a patient requests a
  // specific date.
  [GroupIds.USHV_HEARTPLACE_LIVE]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  [GroupIds.USHV_HEARTPLACE_LIVE_SANDBOX]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  // USHV-MKC: routine referrals search from 7 days out through the EHR's availability horizon
  // ({@link MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS}, ~6 months), including custom date requests. The
  // window stops at that horizon rather than a rounder number so every date the config admits is
  // actually searchable — see MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS for what going past it breaks.
  [GroupIds.USHV_MKC_LIVE]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  [GroupIds.USHV_MKC_LIVE_SANDBOX]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: MAX_SEARCHABLE_LOOKUP_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  // UVVC: routine referrals search from 2 through 90 days out, including custom date requests.
  [GroupIds.UNITED_VEIN_AND_VASCULAR_CENTERS_LIVE]: {
    startOffsetDays: 2,
    maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  [GroupIds.UNITED_VEIN_AND_VASCULAR_CENTERS_LIVE_SANDBOX]: {
    startOffsetDays: 2,
    maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  // Sport Ortho: only Routine referrals (no Urgent/STAT), same-day booking allowed (2-hour min lead
  // time enforced separately). Routine window starts today through 90 days out.
  [GroupIds.SPORTORTHO_LIVE]: { startOffsetDays: 0, maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS },
  [GroupIds.SPORTORTHO_LIVE_SANDBOX]: { startOffsetDays: 0, maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS },
  // ENT Partners (Chicago ENT + Specialty Care ENT): routine starts 2 days out, through 30 days out.
  [GroupIds.ENT_PARTNERS_LIVE]: { startOffsetDays: 2, maxWindowDays: 30 },
  [GroupIds.ENT_PARTNERS_LIVE_SANDBOX]: { startOffsetDays: 2, maxWindowDays: 30 },
  [GroupIds.ENT_PARTNERS_SCI_LIVE]: { startOffsetDays: 2, maxWindowDays: 30 },
  [GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX]: { startOffsetDays: 2, maxWindowDays: 30 },
  // TPR: routine referrals run from 7 through 90 days out, including when the patient requests an earlier date.
  [GroupIds.TPR_BILTMORE_LIVE]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
  [GroupIds.TPR_BILTMORE_LIVE_SANDBOX]: {
    startOffsetDays: DEFAULT_PRIORITY_MAX_DAYS_OUT_END_DATE,
    maxWindowDays: DEFAULT_ROUTINE_LOOKUP_MAX_WINDOW_DAYS,
    enforceWindowOnCustomDate: true,
  },
};

export function getRoutineLookupWindow(groupId: string): LookupWindow {
  return ROUTINE_LOOKUP_WINDOW_BY_GROUP_ID[groupId] ?? DEFAULT_ROUTINE_LOOKUP_WINDOW;
}

/** Groups not listed have no additional lead time beyond rejecting elapsed slots. */
export const DEFAULT_MINIMUM_SLOT_LEAD_TIME_MINUTES = 0;
export const SPORT_ORTHO_MINIMUM_SLOT_LEAD_TIME_MINUTES = 120;
export const TPR_MINIMUM_SLOT_LEAD_TIME_MINUTES = 120;
export const ENT_PARTNERS_MINIMUM_SLOT_LEAD_TIME_MINUTES = 2 * 24 * 60;

/**
 * Per-group minimum lead time: slots starting sooner than this many minutes from now are dropped
 * from availability. Prevents offering slots the patient can't realistically make.
 */
const MINIMUM_SLOT_LEAD_TIME_MINUTES_BY_GROUP_ID: Record<string, number> = {
  // ENT Partners needs two days to validate an appointment before the visit.
  [GroupIds.ENT_PARTNERS_LIVE]: ENT_PARTNERS_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  [GroupIds.ENT_PARTNERS_LIVE_SANDBOX]: ENT_PARTNERS_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  [GroupIds.ENT_PARTNERS_SCI_LIVE]: ENT_PARTNERS_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  [GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX]: ENT_PARTNERS_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  // USHV-Pima: don't offer slots less than 2 hours out.
  [GroupIds.USHV_PIMA_LIVE]: 120,
  [GroupIds.USHV_PIMA_LIVE_SANDBOX]: 120,
  // Sport Ortho: same-day slots are only bookable when at least 2 hours away (per recommended-appointments.md §8).
  [GroupIds.SPORTORTHO_LIVE]: SPORT_ORTHO_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  [GroupIds.SPORTORTHO_LIVE_SANDBOX]: SPORT_ORTHO_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  // TPR Biltmore: give patients at least two hours before an offered appointment.
  [GroupIds.TPR_BILTMORE_LIVE]: TPR_MINIMUM_SLOT_LEAD_TIME_MINUTES,
  [GroupIds.TPR_BILTMORE_LIVE_SANDBOX]: TPR_MINIMUM_SLOT_LEAD_TIME_MINUTES,
};

export function getMinimumSlotLeadTimeMinutes(groupId: string): number {
  return MINIMUM_SLOT_LEAD_TIME_MINUTES_BY_GROUP_ID[groupId] ?? DEFAULT_MINIMUM_SLOT_LEAD_TIME_MINUTES;
}

/** Groups whose configured lead time also applies to SMS and background scheduling requests. */
const ALL_SOURCES_MINIMUM_SLOT_LEAD_TIME_GROUP_IDS = new Set<string>([
  GroupIds.TPR_BILTMORE_LIVE,
  GroupIds.TPR_BILTMORE_LIVE_SANDBOX,
]);

export function enforcesMinimumSlotLeadTimeForAllSources(groupId: string): boolean {
  return ALL_SOURCES_MINIMUM_SLOT_LEAD_TIME_GROUP_IDS.has(groupId);
}
